import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    FaSearch, 
    FaCopy, 
    FaFileExcel, 
    FaPrint, 
    FaFilePdf,
    FaArrowUp,
    FaArrowDown,
    FaFileAlt,
    FaTimes
} from 'react-icons/fa';

const ZpHeadSocietyChallanList = () => {
    const [challans, setChallans] = useState([]);
    const [allWorks, setAllWorks] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('interested');
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedChallan, setSelectedChallan] = useState(null);
    const [updateStatus, setUpdateStatus] = useState('Approved');
    const [updateRemarks, setUpdateRemarks] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        // Fetch WorkNames for lookup
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const worksMap = {};
                Object.values(data).forEach(meetingWorks => {
                    Object.values(meetingWorks).forEach(work => {
                        worksMap[work.workId || work.workNumber] = work;
                    });
                });
                setAllWorks(worksMap);
            }
        });

        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');
        const unsubSoc = onValue(socRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedChallans = [];

                Object.entries(data).forEach(([key, user]) => {
                    const workPayments = user.work_payment;
                    if (workPayments) {
                        const userName = user.societyName || user.userName || "मजूर संस्था";
                        const mobile = user.whatsappNo || user.mobile || "N/A";
                        const taluka = user.taluka || "N/A";

                        Object.entries(workPayments).forEach(([paymentId, payment]) => {
                            flattenedChallans.push({
                                id: `${key}_${paymentId}`,
                                userKey: key,
                                paymentId: paymentId,
                                userName,
                                mobile,
                                taluka,
                                date: payment.date || "N/A",
                                status: payment.status || "Approved",
                                remarks: payment.remarks || "",
                                selectedWorks: payment.selectedWorks || []
                            });
                        });
                    }
                });

                setChallans(flattenedChallans.reverse());
            } else {
                setChallans([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubSoc();
        };
    }, [navigate]);

    const filteredData = challans.filter(item => {
        const searchStr = searchTerm.toLowerCase();
        return (
            item.userName.toLowerCase().includes(searchStr) ||
            item.mobile.toLowerCase().includes(searchStr) ||
            item.taluka.toLowerCase().includes(searchStr)
        );
    });

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr === "N/A") return "-";
        try {
            const date = new Date(dateStr);
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return dateStr;
        }
    };

    const handleActionClick = (challan) => {
        setSelectedChallan(challan);
        setUpdateStatus(challan.status || 'Approved');
        setUpdateRemarks(challan.remarks || '');
        setIsModalOpen(true);
    };

    const handleUpdateStatus = async () => {
        if (!selectedChallan) return;
        setIsUpdating(true);
        try {
            const paymentRef = ref(db, `ekamwatapzp/SocietyRegistrations/${selectedChallan.userKey}/work_payment/${selectedChallan.paymentId}`);
            await update(paymentRef, {
                status: updateStatus,
                remarks: updateRemarks
            });
            toast.success("स्थिती यशस्वीरित्या अपडेट केली!");
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("अपडेट करताना त्रुटी आली!");
        } finally {
            setIsUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            {/* Header / Breadcrumb */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">वर्क चलन</h1>
                <div className="text-[11px] md:text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400 font-bold">वर्क चलन</span>
                </div>
            </div>

            {/* Main Content Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                
                {/* Tabs Section */}
                <div className="border-b border-gray-100 px-6 pt-6 flex flex-wrap gap-4 md:gap-8">
                    <button 
                        onClick={() => setActiveTab('interested')}
                        className={`pb-4 text-xs md:text-sm font-bold transition-all border-b-2 ${activeTab === 'interested' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                    >
                        कामामध्ये इंटरेस्टेड दाखवलेले चलन
                    </button>
                    <button 
                        onClick={() => setActiveTab('agreement')}
                        className={`pb-4 text-xs md:text-sm font-bold transition-all border-b-2 ${activeTab === 'agreement' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                    >
                        प्लॅन्ट करारपत्रक
                    </button>
                    <button 
                        onClick={() => setActiveTab('fee')}
                        className={`pb-4 text-xs md:text-sm font-bold transition-all border-b-2 ${activeTab === 'fee' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                    >
                        वर्क / निविदा फॉर्म फी चलन
                    </button>
                    
                    <div className="ml-auto pb-4 flex items-center gap-2 text-[11px] md:text-xs font-bold text-gray-500 italic">
                        <FaFileAlt className="text-gray-400" />
                        चलन अपलोड केले
                    </div>
                </div>

                {/* Toolbar Section */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-1.5 self-start">
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Copy</button>
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Excel</button>
                        <button className="flex items-center gap-2 px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-sm hover:bg-gray-700 transition">Print</button>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <span className="text-sm font-bold text-gray-500">Search:</span>
                        <input 
                            type="text" 
                            className="flex-grow md:w-64 px-4 py-2 border border-gray-200 rounded text-sm font-medium focus:ring-2 focus:ring-blue-500/20 outline-none transition bg-white"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Data Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-y border-gray-200">
                            <tr>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center w-16">
                                    क्र. <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">
                                    अर्जदाराचे नाव <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    तालुका <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r">
                                    नोंदणी दिनांक <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest border-r text-center">
                                    चलन स्टेटस <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                                <th className="px-5 py-4 text-xs font-black text-gray-500 uppercase tracking-widest text-center">
                                    Action <span className="inline-flex flex-col ml-2 opacity-30"><FaArrowUp className="text-[8px] mb-[-2px]"/><FaArrowDown className="text-[8px]"/></span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 italic">
                            {filteredData.map((item, index) => (
                                <tr key={item.id} className="hover:bg-blue-50/20 transition-all font-medium text-gray-700">
                                    <td className="px-5 py-4 border-r text-center text-sm font-bold">{index + 1}</td>
                                    <td className="px-5 py-4 border-r text-sm">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-800">{item.userName}</span>
                                            <span className="text-xs font-bold text-gray-500 mt-0.5">{item.mobile}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 border-r text-center text-sm font-bold">{item.taluka}</td>
                                    <td className="px-5 py-4 border-r text-sm font-bold text-gray-800">
                                        {formatDate(item.date)}
                                    </td>
                                    <td className="px-5 py-4 border-r text-center">
                                        <span className={`font-black text-sm uppercase ${
                                            item.status === 'Rejected' ? 'text-red-500' : 
                                            item.status === 'Pending' ? 'text-amber-500' : 'text-[#198754]'
                                        }`}>
                                            {item.status || 'Approved'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-center">
                                        <div className="flex justify-center">
                                            <FaFileAlt 
                                                className="text-gray-400 hover:text-gray-600 cursor-pointer transition text-lg" 
                                                onClick={() => handleActionClick(item)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {filteredData.length === 0 && (
                        <div className="py-12 text-center text-gray-400 font-bold bg-[#f8f9fa] border-b border-gray-100">
                            No data available in table
                        </div>
                    )}
                </div>

                {/* Footer Section */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                    <div className="text-sm font-medium text-gray-500">
                        Showing {filteredData.length === 0 ? "0 to 0 of 0" : `1 to ${filteredData.length} of ${filteredData.length}`} entries
                    </div>
                    <div className="flex border border-gray-200 rounded overflow-hidden">
                        <button className="px-4 py-2 text-sm font-black text-gray-400 bg-gray-50/50 hover:bg-gray-100 disabled:opacity-50 border-r border-gray-200" disabled>Previous</button>
                        <button className="px-4 py-2 text-sm font-black text-gray-400 bg-gray-50/50 hover:bg-gray-100 disabled:opacity-50" disabled>Next</button>
                    </div>
                </div>
            </div>

            {/* Challan Detail Modal */}
            {isModalOpen && selectedChallan && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-300">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">Challan details</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition">
                                <FaTimes className="text-xl" />
                            </button>
                        </div>

                        <div className="p-8 space-y-8">
                            {/* Work Challan Details Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-bold text-gray-500">Work Challan Details -</h3>
                                <div className="w-12 h-14 border border-red-200 rounded flex items-center justify-center text-red-500 shadow-sm hover:bg-red-50 cursor-pointer transition-all">
                                    <FaFilePdf className="text-2xl" />
                                </div>
                            </div>

                            {/* Works Table */}
                            <div className="overflow-x-auto rounded-lg border border-gray-100 italic">
                                <table className="w-full text-left">
                                    <thead className="bg-[#f8f9fa] border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-black text-gray-500 uppercase tracking-widest border-r w-16 text-center">Sr.</th>
                                            <th className="px-4 py-3 text-xs font-black text-gray-500 uppercase tracking-widest border-r">Work Id</th>
                                            <th className="px-4 py-3 text-xs font-black text-gray-500 uppercase tracking-widest border-r">कामाचे नाव</th>
                                            <th className="px-4 py-3 text-xs font-black text-gray-500 uppercase tracking-widest border-r">निविदा रक्कम</th>
                                            <th className="px-4 py-3 text-xs font-black text-gray-500 uppercase tracking-widest text-center">तालुका</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {selectedChallan.selectedWorks.map((workStr, idx) => {
                                            const workId = workStr.split(' - ')[0].trim();
                                            const workDetails = allWorks[workId] || {};
                                            return (
                                                <tr key={idx} className="hover:bg-blue-50/10 transition-colors font-medium text-gray-700">
                                                    <td className="px-4 py-3 border-r text-center text-sm font-bold">{idx + 1}</td>
                                                    <td className="px-4 py-3 border-r text-sm font-bold">{workId}</td>
                                                    <td className="px-4 py-3 border-r text-sm">{workDetails.workName || workStr.split(' - ')[1] || '-'}</td>
                                                    <td className="px-4 py-3 border-r text-sm font-bold">{workDetails.tenderAmount || '-'}</td>
                                                    <td className="px-4 py-3 text-center text-sm font-bold">{workDetails.taluka || '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Status Update Form */}
                            <div className="space-y-6 pt-4 border-t border-gray-100">
                                <div className="space-y-2">
                                    <label className="text-sm font-black text-gray-700">Status <span className="text-red-500">*</span></label>
                                    <select 
                                        className="w-full md:w-1/2 p-3 border border-gray-200 rounded-lg text-sm font-bold bg-white focus:ring-2 focus:ring-blue-500/20 outline-none transition appearance-none cursor-pointer"
                                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23a1a1aa\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5em' }}
                                        value={updateStatus}
                                        onChange={(e) => setUpdateStatus(e.target.value)}
                                    >
                                        <option value="Approved">Approved</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-black text-gray-700">Remarks</label>
                                    <textarea 
                                        className="w-full p-4 border border-gray-200 rounded-lg text-sm font-medium h-24 focus:ring-2 focus:ring-blue-500/20 outline-none transition bg-white resize-none"
                                        value={updateRemarks}
                                        onChange={(e) => setUpdateRemarks(e.target.value)}
                                    ></textarea>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button 
                                        onClick={handleUpdateStatus}
                                        disabled={isUpdating}
                                        className="px-6 py-2.5 bg-[#4e73df] hover:bg-blue-700 text-white rounded-md text-sm font-bold transition-all shadow-md transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50"
                                    >
                                        {isUpdating ? "अपडेट होत आहे..." : "Update Status"}
                                    </button>
                                    <button 
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-6 py-2.5 bg-[#1cc88a] hover:bg-green-600 text-white rounded-md text-sm font-bold transition-all shadow-md transform hover:-translate-y-0.5 active:translate-y-0"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ZpHeadSocietyChallanList;
